import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
    currentShare,
    reportApi,
    reportKeys,
    reportPdfFilename,
    type ReportShare,
    type ReportShareChanges,
} from '@/entities/report';

import { offerDownload } from './browser';

/**
 * De kantoorpreview van de klantpagina, vanuit de keuring.
 *
 * Het rapport komt van `GET /orders/{order}/report`. De klantpagina hoort bij
 * de versie die de **koper** heeft (`released`): na "Nieuwe versie maken" is
 * dat de verstuurde versie, niet het nieuwe concept. Link, tellingen,
 * instellingen, opnieuw versturen en de PDF gaan daarom allemaal over die
 * versie. De pagina zelf komt van `GET /reports/{report}/preview`
 * (`usePublicReportPreview`): wat de koper krijgt, zonder bezoek te tellen en
 * zonder toegangscode.
 *
 * Een toegangscode die bij een wijziging ontstaat, staat alleen in het geheugen
 * van dit scherm: `issuedCode`, tot iemand hem wegklikt. `issuedCodeSent` zegt
 * of hij al per sms naar de ontvangers ging (opnieuw versturen met een nieuwe
 * link) of dat kantoor hem zelf moet doorgeven.
 */
export function useClientPagePreview(orderUuid: string) {
    const queryClient = useQueryClient();
    const [issued, setIssued] = useState<{ code: string; sent: boolean } | null>(null);

    const report = useQuery({
        queryKey: reportKeys.forOrder(orderUuid),
        queryFn: () => reportApi.forOrder(orderUuid),
        enabled: orderUuid !== '',
        staleTime: 0,
    });

    const newest = report.data?.report ?? null;
    const released = report.data?.released ?? null;
    const releasedUuid = released?.reportUuid ?? '';

    const statistics = useQuery({
        queryKey: reportKeys.statistics(releasedUuid),
        queryFn: () => reportApi.statistics(releasedUuid),
        enabled: released !== null,
        staleTime: 0,
    });

    const shares = useQuery({
        queryKey: reportKeys.shares(releasedUuid),
        queryFn: () => reportApi.shares(releasedUuid),
        enabled: released !== null && (newest?.can.share ?? false),
        staleTime: 0,
    });

    const share = shares.data ? currentShare(shares.data) : null;
    const url = share?.url ?? statistics.data?.url ?? released?.link?.url ?? null;

    /** Na een wijziging aan de link kloppen tellingen, lijst, pagina en werkende link niet meer. */
    const refreshLink = async (changed?: ReportShare) => {
        if (changed) {
            queryClient.setQueryData<ReportShare[]>(reportKeys.shares(releasedUuid), (current) =>
                current?.map((item) => (item.uuid === changed.uuid ? changed : item)),
            );
        }

        await Promise.all([
            queryClient.invalidateQueries({ queryKey: reportKeys.shares(releasedUuid) }),
            queryClient.invalidateQueries({ queryKey: reportKeys.statistics(releasedUuid) }),
            queryClient.invalidateQueries({ queryKey: reportKeys.forOrder(orderUuid) }),
            queryClient.invalidateQueries({ queryKey: ['public-report'] }),
        ]);
    };

    const updateShare = useMutation({
        mutationFn: (changes: ReportShareChanges) => {
            if (!share) {
                throw new Error('Er is geen link om te wijzigen.');
            }

            return reportApi.updateShare(releasedUuid, share.uuid, changes);
        },
        onSuccess: async (issued, changes) => {
            if (issued.accessCode) {
                setIssued({ code: issued.accessCode, sent: false });
            } else if (changes.removeAccessCode) {
                setIssued(null);
            }

            await refreshLink(issued.share);
        },
        onError: () => refreshLink(),
    });

    const createShare = useMutation({
        mutationFn: () => reportApi.createShare(releasedUuid),
        onSuccess: async (made) => {
            setIssued(made.accessCode ? { code: made.accessCode, sent: false } : null);
            await refreshLink();
        },
    });

    /** De PDF uit de downloadknop van de preview, met de eigen sessie. */
    const pdf = useMutation({
        mutationFn: async () => {
            if (!newest || !released) {
                throw new Error('Er is geen verstuurd rapport om te downloaden.');
            }

            const { blob } = await reportApi.pdf(released.reportUuid);

            return {
                blob,
                filename: reportPdfFilename({
                    orderNumber: newest.orderNumber,
                    version: released.version,
                }),
            };
        },
        onSuccess: ({ blob, filename }) => offerDownload(blob, filename),
    });

    const resend = useMutation({
        mutationFn: () => reportApi.send(releasedUuid),
        onSuccess: async (result) => {
            if (result.newLink) {
                setIssued(
                    result.link.accessCode ? { code: result.link.accessCode, sent: true } : null,
                );
            }

            await refreshLink();
        },
    });

    return {
        report,
        /** De nieuwste versie; kan een concept zijn dat de koper nog niet heeft. */
        newest,
        /** De versie die de koper heeft, of `null` als er nog niets is verstuurd. */
        released,
        statistics,
        shares,
        share,
        url,
        issuedCode: issued?.code ?? null,
        issuedCodeSent: issued?.sent ?? false,
        dismissCode: () => setIssued(null),
        updateShare,
        createShare,
        resend,
        pdf,
    };
}
